import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AppShot } from '../components/AppShot';

const views = [
  {
    id: 'board',
    label: 'Board',
    src: '/images/app/board.jpg',
    alt: 'Kanban board for the Acme Insurance workspace, with New, In Progress, In Review, and Blocked columns',
    note: 'Drag a card between columns. Tags from different dimensions sit on the same card.',
  },
  {
    id: 'list',
    label: 'List',
    src: '/images/app/list.jpg',
    alt: 'List view of work items in the Acme Insurance workspace',
    note: 'The same items as rows, when a board is the wrong shape for the conversation.',
  },
  {
    id: 'timeline',
    label: 'Timeline',
    src: '/images/app/timeline.jpg',
    alt: 'Timeline of dated work items in July and August 2026',
    note: 'Items with dates land on a range. The demo shows nine items, some without dates.',
  },
  {
    id: 'calendar',
    label: 'Calendar',
    src: '/images/app/calendar.jpg',
    alt: 'October 2026 calendar for the Acme Insurance workspace',
    note: 'A month grid for due dates. Items without dates are counted beside the calendar.',
  },
  {
    id: 'workload',
    label: 'Workload',
    src: '/images/app/workload.jpg',
    alt: 'Workload summary: five people, 150 estimated hours, 14.33 actual hours, one person over capacity',
    note: 'Capacity is 40 hours a person. Estimated load is compared with hours already tracked.',
  },
];

const stops = [
  { id: 'home', label: 'Home' },
  { id: 'workspaces', label: 'Workspaces' },
  { id: 'views', label: 'Views' },
  { id: 'pages', label: 'Pages' },
  { id: 'dimensions', label: 'Dimensions' },
  { id: 'portfolio', label: 'Portfolio' },
  { id: 'automation', label: 'Automation' },
  { id: 'assistant', label: 'Assistant' },
];

export const ProductTour: React.FC = () => {
  const [view, setView] = useState(views[0]);

  return (
    <div className="pb-20 pt-28">
      <div className="container-width section-padding">
        <p className="eyebrow">Product tour</p>
        <h1 className="max-w-3xl font-display text-4xl sm:text-5xl">Screens from the demo organization.</h1>
        <p className="lede mt-5">
          These are the app, signed in, not illustrations. The organization is on the Forest theme:
          deep green sidebar, paper canvas, serif titles. Your organization can pick another theme
          in settings.
        </p>

        <nav aria-label="Tour sections" className="mt-8 flex flex-wrap gap-2">
          {stops.map((stop) => (
            <a key={stop.id} href={`#${stop.id}`} className="btn-glass px-3 py-2 text-sm">
              {stop.label}
            </a>
          ))}
        </nav>

        <section id="home" className="mt-14 scroll-mt-24">
          <h2 className="font-display text-3xl">Home</h2>
          <p className="mt-3 max-w-2xl text-ink-soft">
            The day starts with counts for completed, in progress, blocked, and overdue, then the
            items across workspaces. Search is here, and also in the command palette with ⌘K.
          </p>
          <div className="mt-6">
            <AppShot
              src="/images/app/home.jpg"
              alt="Home with status counts and a cross-workspace list of work items"
            />
          </div>
        </section>

        <section id="workspaces" className="mt-16 scroll-mt-24">
          <h2 className="font-display text-3xl">Workspaces</h2>
          <p className="mt-3 max-w-2xl text-ink-soft">
            The demo holds four: a policy portal, a fleet dashboard, a travel ops console, and a
            field-operations board. Each one has its own members, pages, and budget.
          </p>
          <div className="mt-6">
            <AppShot
              src="/images/app/workspaces.jpg"
              alt="Four workspace cards in the demo organization"
            />
          </div>
        </section>

        <section id="views" className="mt-16 scroll-mt-24">
          <h2 className="font-display text-3xl">Five views</h2>
          <p className="mt-3 max-w-2xl text-ink-soft">
            Same workspace, Acme Insurance — Policy Portal. The budget panel stays put. The view
            underneath changes.
          </p>
          <div className="mt-5 flex flex-wrap gap-2" role="tablist" aria-label="Workspace views">
            {views.map((item) => {
              const selected = item.id === view.id;
              return (
                <button
                  key={item.id}
                  id={`view-tab-${item.id}`}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  aria-controls="view-panel"
                  className={selected ? 'btn-premium' : 'btn-glass'}
                  onClick={() => setView(item)}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
          <div id="view-panel" role="tabpanel" aria-labelledby={`view-tab-${view.id}`} className="mt-4">
            <p className="text-ink-soft">{view.note}</p>
            <div className="mt-4">
              <AppShot key={view.id} src={view.src} alt={view.alt} />
            </div>
          </div>
        </section>

        <section id="pages" className="mt-16 scroll-mt-24">
          <h2 className="font-display text-3xl">Pages</h2>
          <p className="mt-3 max-w-2xl text-ink-soft">
            A workspace can hold a tree of documents: folders and markdown. The Acme demo had not
            started one, so this is the empty state, not a mockup of a finished manual.
          </p>
          <div className="mt-6">
            <AppShot
              src="/images/app/pages.jpg"
              alt="Empty pages screen for the Acme Insurance workspace, with a New page button"
            />
          </div>
        </section>

        <section id="dimensions" className="mt-16 scroll-mt-24">
          <h2 className="font-display text-3xl">Dimensions</h2>
          <p className="mt-3 max-w-2xl text-ink-soft">
            Business Units, Category, and Priority. Open one to edit its tags. Cards on the board
            show tags from more than one dimension at once.
          </p>
          <div className="mt-6">
            <AppShot
              src="/images/app/dimensions.jpg"
              alt="Three dimensions: Business Units, Category, and Priority"
            />
          </div>
        </section>

        <section id="portfolio" className="mt-16 scroll-mt-24">
          <h2 className="font-display text-3xl">Portfolio</h2>
          <p className="mt-3 max-w-2xl text-ink-soft">
            Health, open, overdue, blocked, hours, and budget burn for every workspace. This shot
            is the demo on 6 October 2026: 28 open items, 24 overdue, 660 estimated hours.
          </p>
          <div className="mt-6">
            <AppShot
              src="/images/app/portfolio.jpg"
              alt="Portfolio rollup for the four demo workspaces"
            />
          </div>
        </section>

        <section id="automation" className="mt-16 scroll-mt-24">
          <h2 className="font-display text-3xl">Automation</h2>
          <p className="mt-3 max-w-2xl text-ink-soft">
            Rules, a template library, and an execution history. The library is grouped and
            searchable. Start from a template or build a rule from a trigger and one or more actions.
          </p>
          <div className="mt-6">
            <AppShot
              src="/images/app/automation.jpg"
              alt="Featured automation templates including blocked-work escalation and bug triage"
            />
          </div>
        </section>

        <section id="assistant" className="mt-16 scroll-mt-24">
          <h2 className="font-display text-3xl">Assistant</h2>
          <p className="mt-3 max-w-2xl text-ink-soft">
            Pick the workspace, then ask. The prompts on the empty state are the ones the product
            ships with. It will not email a client status unless you take that step yourself.
          </p>
          <div className="mt-6">
            <AppShot
              src="/images/app/assistant.jpg"
              alt="Assistant ready to summarize a workspace or draft a client status"
            />
          </div>
          <p className="mt-8">
            <Link to="/features" className="font-semibold text-forest">Feature notes</Link>
            <span className="text-ink-soft"> sit beside these screens. </span>
            <Link to="/pricing" className="font-semibold text-forest">Pricing</Link>
            <span className="text-ink-soft"> is two plans.</span>
          </p>
        </section>
      </div>
    </div>
  );
};
